/**
 * 筋特征提交执行处理器。
 */

import { RibFeatureDefinitionModel } from '../../../definition/rib/RibFeatureDefinition';
import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { RibFeaturePrototype } from '../../../prototype/rib/RibFeaturePrototype';
import type { FeatureExecutionRuntime } from '../../FeatureExecutionRuntime';
import {
    FeatureCommitExecutionHandler,
    type FeatureCommitExecutionInput,
    type FeatureCommitExecutionResult,
} from '../FeatureCommitExecutionHandler';
import { RibCommitExecution } from './RibCommitExecution';

/**
 * 筋特征提交执行处理器。
 */
export class RibCommitExecutionHandler extends FeatureCommitExecutionHandler {
    /** 筋提交执行器。 */
    private readonly execution: RibCommitExecution;
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;

    /**
     * @param execution 筋提交执行器。
     * @param runtime 执行运行时。
     */
    public constructor(execution: RibCommitExecution, runtime: FeatureExecutionRuntime) {
        super();
        this.execution = execution;
        this.runtime = runtime;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeatureCommitExecutionInput): boolean {
        return input.featureKind === 'rib' && input.definition instanceof RibFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当提交输入中的定义不是筋特征定义时。
     */
    public async execute(input: FeatureCommitExecutionInput): Promise<FeatureCommitExecutionResult> {
        if (!(input.definition instanceof RibFeatureDefinitionModel)) {
            throw new Error('筋提交处理器收到的 definition 类型不正确');
        }

        const featureId = input.featureId ?? `feature:${Date.now()}`;
        const prototype = new RibFeaturePrototype({
            featureId,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId: null,
        });

        if (input.session) {
            const replaced = await this.execution.commitEdit(
                input.session as FeatureEditSession<RibFeatureDefinitionModel, RibFeaturePrototype>,
                prototype,
            );
            return {
                replaced,
                featureId,
                prototype,
                meshData: null,
            };
        }

        const objectId = await this.execution.commitCreate(prototype);
        const meshData = await this.runtime.generateMeshData(objectId);
        return {
            replaced: false,
            featureId,
            prototype,
            meshData,
            objectId,
        };
    }
}
